import React, { useState, useEffect, useCallback, type ReactNode } from 'react';
import { keyframes } from '@emotion/react';
import { alpha } from '@mui/material/styles';
import { createPortal } from 'react-dom';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
// import { useSettings } from '../contexts/SettingsContext'; // dark mode toggle — re-enable when dark mode is ready
import { useSubscription } from '../contexts/SubscriptionContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  MenuItem,
  useTheme,
  useMediaQuery,
  Avatar,
  Tooltip,
  Button,
  Divider,
  // Switch, // dark mode toggle — re-enable when dark mode is ready
} from '@mui/material';
import { ClayChip } from './ClayChip';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import HistoryIcon from '@mui/icons-material/History';
import LogoutIcon from '@mui/icons-material/Logout';
import HomeIcon from '@mui/icons-material/Home';
import SettingsIcon from '@mui/icons-material/Settings';
import PersonIcon from '@mui/icons-material/Person';
// import DarkModeIcon from '@mui/icons-material/DarkMode'; // dark mode toggle — re-enable when dark mode is ready
// import LightModeIcon from '@mui/icons-material/LightMode'; // dark mode toggle — re-enable when dark mode is ready
import NoteIcon from '@mui/icons-material/Note';
import EventNoteIcon from '@mui/icons-material/EventNote';
import BugReportIcon from '@mui/icons-material/BugReport';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import BugReportModal from './BugReportModal';
import { menuSectionLabelSx, navMenuItemSx } from '../utils/styleHelpers';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const summarizeRect = (element: HTMLElement | null) => {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  return {
    top: Math.round(rect.top),
    left: Math.round(rect.left),
    width: Math.round(rect.width),
    height: Math.round(rect.height),
  };
};

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const backdropFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const backdropFadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const drawerSlideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

const drawerSlideOut = keyframes`
  from { transform: translateX(0); }
  to   { transform: translateX(100%); }
`;

const ANIMATION_TIMINGS = {
  enterBackdrop: 120,
  exitBackdrop: 150,
  enterSheet: 220,
  exitSheet: 200,
} as const;

// ---------------------------------------------------------------------------
// MobileMenuPortal
// ---------------------------------------------------------------------------

type MobileMenuPortalProps = {
  open: boolean;
  onClose: (reason?: string) => void;
  children: ReactNode;
};

const MobileMenuPortal = ({ open, onClose, children }: MobileMenuPortalProps) => {
  const theme = useTheme();
  const [hasMounted, setHasMounted] = useState(false);
  const [renderPortal, setRenderPortal] = useState(open);
  const [animationState, setAnimationState] = useState<'enter' | 'exit'>('enter');

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    if (open) {
      setRenderPortal(true);
      setAnimationState('enter');
      return;
    }
    if (!open && renderPortal) {
      setAnimationState('exit');
      const timeout = setTimeout(() => setRenderPortal(false), ANIMATION_TIMINGS.exitSheet);
      return () => clearTimeout(timeout);
    }
  }, [open, renderPortal]);

  useEffect(() => {
    if (!renderPortal) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [renderPortal]);

  useEffect(() => {
    if (!renderPortal) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose('escapeKeyDown');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [renderPortal, onClose]);

  if (!hasMounted || !renderPortal) return null;

  const backdropAnimation =
    animationState === 'enter'
      ? `${backdropFadeIn} ${ANIMATION_TIMINGS.enterBackdrop}ms ease-out forwards`
      : `${backdropFadeOut} ${ANIMATION_TIMINGS.exitBackdrop}ms ease-in forwards`;

  const sheetAnimation =
    animationState === 'enter'
      ? `${drawerSlideIn} ${ANIMATION_TIMINGS.enterSheet}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`
      : `${drawerSlideOut} ${ANIMATION_TIMINGS.exitSheet}ms cubic-bezier(0.55, 0, 0.1, 1) forwards`;

  return createPortal(
    <Box
      role="presentation"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: theme.zIndex.modal + 1,
        backgroundColor: 'rgba(0, 0, 0, 0.45)',
        animation: backdropAnimation,
      }}
      onClick={() => onClose('backdropClick')}
    >
      <Box
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          height: '100%',
          width: '75%',
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.96)
            : alpha(theme.palette.background.paper, 0.97),
          backdropFilter: 'blur(24px) saturate(180%)',
          WebkitBackdropFilter: 'blur(24px) saturate(180%)',
          borderTopLeftRadius: 28,
          borderBottomLeftRadius: 28,
          boxShadow: [
            '-10px 0 50px rgba(0,0,0,0.28)',
            '-4px 0 18px rgba(0,0,0,0.12)',
            'inset 1px 0 0 rgba(255,255,255,0.18)',
            `inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.08)}`,
          ].join(', '),
          overflowY: 'auto',
          animation: sheetAnimation,
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        {children}
      </Box>
    </Box>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// MobileNavMenu
// ---------------------------------------------------------------------------

type NavItem = { text: string; icon: React.ReactNode; path: string };

type MobileNavMenuProps = {
  user: { email?: string | null; displayName?: string | null } | null;
  // darkMode: boolean; // dark mode toggle — re-enable when dark mode is ready
  navItems: NavItem[];
  accountItems: NavItem[];
  isActive: (path: string) => boolean;
  onClose: (reason?: string) => void;
  onMenuItemClick: (path: string) => void;
  // onDarkModeToggle: () => void; // dark mode toggle — re-enable when dark mode is ready
  onLogout: () => void;
  onBugReport: () => void;
};

const MobileNavMenu = ({
  user,
  // darkMode, // dark mode toggle — re-enable when dark mode is ready
  navItems,
  accountItems,
  isActive,
  onClose,
  onMenuItemClick,
  // onDarkModeToggle, // dark mode toggle — re-enable when dark mode is ready
  onLogout,
  onBugReport,
}: MobileNavMenuProps) => {
  const theme = useTheme();

  const SectionLabel = ({ label }: { label: string }) => (
    <Typography variant="caption" sx={menuSectionLabelSx}>
      {label}
    </Typography>
  );

  const NavMenuItemRow = ({ item }: { item: NavItem }) => {
    const active = isActive(item.path);
    return (
      <MenuItem
        key={item.text}
        component={RouterLink}
        to={item.path}
        onClick={() => onMenuItemClick(item.path)}
        sx={{
          ...navMenuItemSx(active),
          ...(active && {
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.14)}, ${alpha(theme.palette.primary.light, 0.08)})`,
            boxShadow: [
              `0 2px 10px ${alpha(theme.palette.primary.main, 0.15)}`,
              'inset 0 1px 0 rgba(255,255,255,0.6)',
              `inset 0 -1px 0 ${alpha(theme.palette.primary.main, 0.1)}`,
            ].join(', '),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          }),
          transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          '&:active': { transform: 'scale(0.98)' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ color: active ? 'primary.main' : 'text.secondary', display: 'flex' }}>
            {item.icon}
          </Box>
          {item.text}
        </Box>
      </MenuItem>
    );
  };

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.18)}, transparent)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, transparent)`,
        }}
      >
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: 'primary.main',
            fontSize: '0.9rem',
            boxShadow: [
              `0 4px 12px ${alpha(theme.palette.primary.main, 0.42)}`,
              '0 2px 5px rgba(0,0,0,0.12)',
              'inset 0 1.5px 0 rgba(255,255,255,0.3)',
              'inset 0 -1.5px 0 rgba(0,0,0,0.15)',
            ].join(', '),
          }}
        >
          {user?.email?.[0]?.toUpperCase() ?? <PersonIcon />}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {user?.displayName || 'My Account'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {user?.email}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={() => onClose('closeButton')}
          sx={{
            color: 'text.secondary',
            borderRadius: '50%',
            width: 30,
            height: 30,
            background: theme.palette.mode === 'dark'
              ? `linear-gradient(145deg, ${theme.palette.grey[700]}, ${theme.palette.grey[800]})`
              : `linear-gradient(145deg, ${theme.palette.background.paper}, ${theme.palette.grey[100]})`,
            boxShadow: [
              '0 2px 8px rgba(0,0,0,0.1)',
              'inset 0 1px 0 rgba(255,255,255,0.8)',
              'inset 0 -1px 0 rgba(0,0,0,0.06)',
            ].join(', '),
            transition: 'transform 0.16s ease, box-shadow 0.16s ease',
            '&:hover': {
              transform: 'scale(1.08)',
              boxShadow: [
                '0 4px 12px rgba(0,0,0,0.14)',
                'inset 0 1px 0 rgba(255,255,255,0.85)',
                'inset 0 -1px 0 rgba(0,0,0,0.07)',
              ].join(', '),
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Navigation */}
      <SectionLabel label="Navigation" />
      {navItems.map((item) => <NavMenuItemRow key={item.text} item={item} />)}

      <Divider sx={{ mx: 2, my: 1 }} />

      {/* Account */}
      <SectionLabel label="Account" />
      {accountItems.map((item) => <NavMenuItemRow key={item.text} item={item} />)}

      <Divider sx={{ mx: 2, my: 1 }} />

      {/* Preferences */}
      {/* Dark mode toggle hidden until dark mode is polished
      <SectionLabel label="Preferences" />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mx: 1,
          px: 1,
          py: 0.5,
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ color: 'text.secondary', display: 'flex' }}>
            {darkMode ? <DarkModeIcon /> : <LightModeIcon />}
          </Box>
          <Typography variant="body2">{darkMode ? 'Dark Mode' : 'Light Mode'}</Typography>
        </Box>
        <Switch size="small" checked={darkMode} onChange={onDarkModeToggle} />
      </Box>
      */}

      <MenuItem onClick={onBugReport} sx={navMenuItemSx(false)}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ color: 'text.secondary', display: 'flex' }}>
            <BugReportIcon />
          </Box>
          Report a Bug
        </Box>
      </MenuItem>

      {/* Logout */}
      <Box sx={{ mt: 'auto', pt: 1, pb: 2 }}>
        <Divider sx={{ mx: 2, mb: 1 }} />
        <MenuItem
          onClick={onLogout}
          sx={{
            ...navMenuItemSx(false),
            color: 'error.main',
            mx: 1.5,
            borderRadius: 2.5,
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            '&:hover': {
              background: alpha(theme.palette.error.main, 0.08),
              boxShadow: [
                `0 2px 10px ${alpha(theme.palette.error.main, 0.18)}`,
                'inset 0 1px 0 rgba(255,255,255,0.55)',
              ].join(', '),
            },
            '&:active': { transform: 'scale(0.98)' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LogoutIcon fontSize="small" />
            Logout
          </Box>
        </MenuItem>
      </Box>
    </>
  );
};

// ---------------------------------------------------------------------------
// Navbar
// ---------------------------------------------------------------------------

export default function Navbar() {
  const { user, logout } = useAuth();
  // const { settings, updateSettings } = useSettings(); // dark mode toggle — re-enable when dark mode is ready
  const { isPremium } = useSubscription();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [menuButtonRect, setMenuButtonRect] = useState<ReturnType<typeof summarizeRect>>(null);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const logMenuDiagnostics = useCallback(
    (event: string, details: Record<string, unknown> = {}) => {
      console.debug(`[Navbar] ${event}`, {
        menuOpen: mobileMenuOpen,
        isDesktop,
        pathname: location.pathname,
        anchorRect: menuButtonRect,
        ...details,
      });
    },
    [mobileMenuOpen, isDesktop, location.pathname, menuButtonRect]
  );

  useEffect(() => { logMenuDiagnostics('menu visibility changed'); }, [mobileMenuOpen, logMenuDiagnostics]);
  useEffect(() => { logMenuDiagnostics('menu button rect updated'); }, [menuButtonRect, logMenuDiagnostics]);
  useEffect(() => { logMenuDiagnostics('breakpoint toggled'); }, [isDesktop, logMenuDiagnostics]);
  useEffect(() => { logMenuDiagnostics('route changed'); }, [location.pathname, logMenuDiagnostics]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    const targetRect = summarizeRect(event.currentTarget);
    setMenuButtonRect(targetRect);
    setMobileMenuOpen(true);
    logMenuDiagnostics('menu button pressed', { targetRect });
  };

  const handleClose = (reason?: 'backdropClick' | 'escapeKeyDown' | string) => {
    logMenuDiagnostics('menu close requested', { reason: reason ?? 'manual' });
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  // const handleDarkModeToggle = async (source?: string) => { // dark mode toggle — re-enable when dark mode is ready
  //   logMenuDiagnostics('dark mode toggled', { nextValue: !settings.darkMode, source: source ?? 'unspecified' });
  //   await updateSettings({ darkMode: !settings.darkMode });
  // };

  const handleMenuItemClick = (path: string) => {
    logMenuDiagnostics('menu item clicked', { path, source: 'mobileMenu' });
    handleClose(`menuItem:${path}`);
  };

  if (!user) return null;

  const isActive = (path: string) => location.pathname === path;

  const navItems: NavItem[] = [
    { text: 'Home', icon: <HomeIcon />, path: '/dashboard' },
    { text: 'Study', icon: <SchoolIcon />, path: '/study-setup' },
    { text: 'AI Study', icon: <AutoStoriesIcon />, path: '/ai-study' },
    { text: 'Upcoming Tests', icon: <EventNoteIcon />, path: '/upcoming-tests' },
    { text: 'Notes', icon: <NoteIcon />, path: '/notes' },
    { text: 'History', icon: <HistoryIcon />, path: '/history' },
  ];

  const accountItems: NavItem[] = [
    { text: 'Profile', icon: <PersonIcon />, path: '/profile' },
    { text: 'Billing', icon: <CreditCardIcon />, path: '/billing' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: 'background.paper',
          borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.40)}`,
          width: '100%',
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Box sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
          <Toolbar
            disableGutters
            sx={{ minHeight: { xs: 48, sm: 64 }, width: '100%', display: 'flex', justifyContent: 'space-between', p: 0 }}
          >
            <Typography
              variant="h6"
              component={RouterLink}
              to="/dashboard"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': { color: 'primary.dark' },
              }}
            >
              <SchoolIcon />
              BrainLoop
            </Typography>

            {!isDesktop ? (
              <>
                <IconButton
                  size="large"
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={handleMenu}
                  sx={{ color: 'text.primary' }}
                >
                  <MenuIcon />
                </IconButton>
                <MobileMenuPortal open={mobileMenuOpen} onClose={handleClose}>
                  <MobileNavMenu
                    user={user}
                    // darkMode={settings.darkMode} // dark mode toggle — re-enable when dark mode is ready
                    navItems={navItems}
                    accountItems={accountItems}
                    isActive={isActive}
                    onClose={handleClose}
                    onMenuItemClick={handleMenuItemClick}
                    // onDarkModeToggle={() => handleDarkModeToggle('mobileMenu')} // dark mode toggle — re-enable when dark mode is ready
                    onLogout={() => {
                      logMenuDiagnostics('logout menu clicked', { source: 'mobileMenu' });
                      handleLogout();
                    }}
                    onBugReport={() => {
                      logMenuDiagnostics('bug report menu clicked', { source: 'mobileMenu' });
                      handleClose('bugReport');
                      setBugReportOpen(true);
                    }}
                  />
                </MobileMenuPortal>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {!isPremium ? (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => navigate('/app/pricing')}
                    sx={{
                      mr: 0.5,
                      borderRadius: 3,
                      textTransform: 'none',
                      fontWeight: 700,
                      background: `linear-gradient(145deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
                      transition: 'transform 0.16s ease, box-shadow 0.16s ease',
                      boxShadow: [
                        `0 4px 14px ${alpha(theme.palette.primary.main, 0.42)}`,
                        '0 2px 5px rgba(0,0,0,0.1)',
                        'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                        `inset 0 -1.5px 0 ${alpha(theme.palette.primary.dark, 0.25)}`,
                      ].join(', '),
                      '&:hover': {
                        transform: 'translateY(-1px) scale(1.02)',
                        boxShadow: [
                          `0 6px 18px ${alpha(theme.palette.primary.main, 0.52)}`,
                          '0 3px 8px rgba(0,0,0,0.12)',
                          'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                        ].join(', '),
                      },
                      '&:active': { transform: 'translateY(1px) scale(0.98)' },
                    }}
                  >
                    Upgrade
                  </Button>
                ) : (
                  <ClayChip
                    label="Pro"
                    color="primary"
                    size="small"
                    sx={{ mr: 0.5 }}
                  />
                )}
                <Tooltip title="Home">
                  <IconButton
                    component={RouterLink}
                    to="/dashboard"
                    color="inherit"
                    sx={{
                      color: isActive('/dashboard') ? 'primary.main' : 'text.primary',
                      borderRadius: '50%',
                      transition: 'transform 0.16s ease, box-shadow 0.16s ease',
                      ...(isActive('/dashboard') && {
                        background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.light, 0.06)})`,
                        boxShadow: [
                          `0 2px 10px ${alpha(theme.palette.primary.main, 0.2)}`,
                          'inset 0 1px 0 rgba(255,255,255,0.6)',
                        ].join(', '),
                      }),
                      '&:hover': {
                        transform: 'translateY(-1px) scale(1.08)',
                        boxShadow: [
                          '0 3px 10px rgba(0,0,0,0.12)',
                          'inset 0 1px 0 rgba(255,255,255,0.7)',
                        ].join(', '),
                      },
                      '&:active': { transform: 'scale(0.94)' },
                    }}
                  >
                    <HomeIcon />
                  </IconButton>
                </Tooltip>
                {/* Dark mode toggle hidden until dark mode is polished
                <Tooltip title={settings.darkMode ? 'Light Mode' : 'Dark Mode'}>
                  <IconButton
                    color="inherit"
                    onClick={() => handleDarkModeToggle('desktopIconButton')}
                    sx={{
                      color: 'text.primary',
                      borderRadius: '50%',
                      transition: 'transform 0.16s ease, box-shadow 0.16s ease',
                      '&:hover': {
                        transform: 'translateY(-1px) scale(1.08)',
                        boxShadow: [
                          '0 3px 10px rgba(0,0,0,0.12)',
                          'inset 0 1px 0 rgba(255,255,255,0.7)',
                        ].join(', '),
                      },
                      '&:active': { transform: 'scale(0.94)' },
                    }}
                  >
                    {settings.darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                  </IconButton>
                </Tooltip>
                */}
                <Tooltip title="Profile">
                  <IconButton
                    color="inherit"
                    onClick={() => navigate('/profile')}
                    sx={{
                      p: 0.5,
                      transition: 'transform 0.16s ease, box-shadow 0.16s ease',
                      '&:hover': { transform: 'translateY(-1px) scale(1.08)' },
                      '&:active': { transform: 'scale(0.94)' },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'primary.main',
                        boxShadow: [
                          `0 3px 10px ${alpha(theme.palette.primary.main, 0.42)}`,
                          '0 1px 4px rgba(0,0,0,0.1)',
                          'inset 0 1.5px 0 rgba(255,255,255,0.28)',
                          'inset 0 -1.5px 0 rgba(0,0,0,0.12)',
                        ].join(', '),
                      }}
                    >
                      <PersonIcon />
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Tooltip title="Logout">
                  <IconButton
                    color="inherit"
                    onClick={handleLogout}
                    sx={{
                      color: 'text.primary',
                      borderRadius: '50%',
                      transition: 'transform 0.16s ease, box-shadow 0.16s ease',
                      '&:hover': {
                        transform: 'translateY(-1px) scale(1.08)',
                        color: 'error.main',
                        boxShadow: [
                          `0 3px 10px ${alpha(theme.palette.error.main, 0.2)}`,
                          'inset 0 1px 0 rgba(255,255,255,0.6)',
                        ].join(', '),
                      },
                      '&:active': { transform: 'scale(0.94)' },
                    }}
                  >
                    <LogoutIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Toolbar>
        </Box>
      </AppBar>

      <BugReportModal
        open={bugReportOpen}
        onClose={() => setBugReportOpen(false)}
        userEmail={user?.email || ''}
      />
    </>
  );
}
